import { Injectable, Injector } from '@angular/core';
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AppAuthService } from './app-auth.service';

/**
 * On a 401, transparently exchange the refresh token for a new access token and
 * retry the request once before letting the error propagate (which is where the
 * ABP interceptor surfaces the session-expired handling). Registered AFTER
 * AbpHttpInterceptor so it sees the raw 401 response first.
 *
 * AppAuthService is resolved lazily via the Injector (only when a 401 actually
 * fires) instead of injected in the constructor. Injecting it eagerly would pull
 * in TokenAuthServiceProxy -> API_BASE_URL during the very first HttpClient call
 * (the appconfig load), caching an empty base URL before remoteServiceBaseUrl is
 * set and sending every API request to the app origin (404).
 */
@Injectable()
export class AuthRefreshInterceptor implements HttpInterceptor {
    private readonly retriedHeader = 'X-Auth-Retried';

    constructor(private injector: Injector) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: unknown) => {
                const isTokenAuthCall = req.url.indexOf('TokenAuth/') !== -1;
                const alreadyRetried = req.headers.has(this.retriedHeader);

                if (
                    error instanceof HttpErrorResponse &&
                    error.status === 401 &&
                    !isTokenAuthCall &&
                    !alreadyRetried
                ) {
                    const authService = this.injector.get(AppAuthService);
                    return authService.refreshAccessToken().pipe(
                        switchMap((newToken) => {
                            if (!newToken) {
                                return throwError(() => error);
                            }

                            const retriedReq = req.clone({
                                setHeaders: {
                                    Authorization: `Bearer ${newToken}`,
                                    [this.retriedHeader]: 'true',
                                },
                            });
                            return next.handle(retriedReq);
                        })
                    );
                }

                return throwError(() => error);
            })
        );
    }
}
